import moment from 'moment'
import {type Anomaly, type FormattedAnomaly} from './types/formatted-anomaly'
import {
    FLEET_VIEW_IDENTIFIER,
    getLocationDescription,
    type Location,
    type LocationMap,
} from '../../../store/state/locations/state'
import {
    type IsolatedAssetsType,
    type IsolationStatusFormatted,
    IsolationStatusType,
} from './types/isolated-assets-type'
import CIRCLE_CHECKED from '../../../@assets/icons/circle-checked.svg'
import CIRCLE_X from '../../../@assets/icons/circle-x.svg'
import CIRCLE_UNKNOWN from '../../../@assets/icons/circle-unknown.svg'
import {NetworkType} from './types/network-type'
import {type NodeData} from '../../../values/nodes/NodeData'
import {BlockStatus} from './types/aggregation-state'

function filterByLocation(anomalies: Anomaly[], activeLocation: Location): Anomaly[] {
    return anomalies.filter((anomaly) => isSelectedLocation(anomaly, activeLocation))
}
function isSelectedLocation(anomaly: Anomaly, activeLocation: Location): boolean {
    return (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        !activeLocation ||
        activeLocation.location === FLEET_VIEW_IDENTIFIER ||
        activeLocation.location === anomaly.location
    )
}
const getFormattedAnomalies = (
    anomalies: Anomaly[],
    locationMap: LocationMap,
    isolatedAssetsStatus: IsolatedAssetsType[] | undefined,
): FormattedAnomaly[] => {
    return anomalies.map((anomaly) => formatAnomaly(anomaly, locationMap, isolatedAssetsStatus))
}

const formatAnomaly = (
    anomaly: Anomaly,
    locationMap: LocationMap,
    isolatedAssetsStatus?: IsolatedAssetsType[],
): FormattedAnomaly => ({
    occurred: moment(anomaly.firstSeen).fromNow(),
    timestamp: moment(anomaly.updatedTimestamp).format(),
    occurredLastSeen: getOccurredLastSeenDisplayValue(anomaly.lastSeen),
    lastSeenTimestamp: anomaly.lastSeen ? moment(anomaly.lastSeen).format() : '',
    ipAddress: anomaly.mainIpAddress,
    location: getLocationDescription(locationMap, anomaly.location),
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    macAddress: anomaly.mainMacAddress ?? 'N/A',
    macDescription: anomaly.mainMacAddressVendor,
    identity: anomaly.identity,
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    vlan: anomaly.vlan ?? 'N/A',

    network: formatNetworkLabel(anomaly.assetNetworkType),
    hostname: anomaly.name,
    isolationStatus: getIsolationStatusOfAsset(isolatedAssetsStatus, anomaly.identity),
    aimeeState: anomaly.aimeeState,
    blockStatus: anomaly.blockStatus,
})

export function setFormattedAnomalies(
    dataUnknownAssets: Anomaly[] | undefined,
    activeLocation: Location,
    locationMap: LocationMap,
    isolatedAssetsStatus: IsolatedAssetsType[] | undefined,
): FormattedAnomaly[] {
    if (dataUnknownAssets == undefined) {
        return new Array<FormattedAnomaly>()
    }
    return getFormattedAnomalies(
        filterByLocation(dataUnknownAssets, activeLocation),
        locationMap,
        isolatedAssetsStatus,
    )
}

export function getBlockStatusOfAsset(
    isolatedAssetsStatus: IsolatedAssetsType[] | undefined,
    assetIdentity: string,
): BlockStatus {
    if (!isolatedAssetsStatus) {
        return BlockStatus.UNDECIDED
    }

    const asset = isolatedAssetsStatus.find((item) => item.identity === assetIdentity)

    if (!asset) {
        return BlockStatus.UNDECIDED
    }

    switch (asset.blockStatus) {
        case BlockStatus.TRUSTED:
            return BlockStatus.TRUSTED
        case BlockStatus.UNDECIDED:
            return BlockStatus.UNDECIDED
        default:
            return asset.blockStatus
    }
}
export function getIsolationStatusOfAsset(
    isolatedAssetsStatus: IsolatedAssetsType[] | undefined,
    assetIdentity: string,
): IsolationStatusFormatted {
    if (!isolatedAssetsStatus) {
        return {
            isolationStatus: IsolationStatusType.UNDECIDED,
            updatedAt: '',
        }
    }
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const findStatusForAsset = isolatedAssetsStatus?.find(
        (isolatedAsset) => isolatedAsset.identity === assetIdentity,
    )
    if (!findStatusForAsset) {
        return {
            isolationStatus: IsolationStatusType.UNDECIDED,
            updatedAt: '',
        }
    }
    return {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        isolationStatus: findStatusForAsset?.isolationStatus,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        updatedAt: findStatusForAsset?.updatedAt,
    }
}

export function getOccurredLastSeenDisplayValue(
    occurredLastSeen: string | null | undefined,
): string {
    return !occurredLastSeen ? 'N/A' : moment(occurredLastSeen).fromNow()
}

export function formatNetworkLabel(networkLabel: string | undefined): NetworkType {
    switch (networkLabel) {
        case 'ot':
            return NetworkType.NetworkTypeOT
        case 'crew':
            return NetworkType.NetworkTypeCrew
        case 'business':
            return NetworkType.NetworkTypeBusiness
        default:
            return NetworkType.NetworkTypeOther
    }
}

export function getImageForBlockStatus(blockStatus: BlockStatus): string {
    switch (blockStatus) {
        case BlockStatus.TRUSTED:
            return CIRCLE_CHECKED
        case BlockStatus.BLOCKED:
            return CIRCLE_X
        case BlockStatus.UNDECIDED:
            return CIRCLE_UNKNOWN
        default:
            return ''
    }
}

export function getAssetStatusForNoAc(
    acEnabled: boolean,
    networkAssetBlockStatus: BlockStatus[] | undefined,
): BlockStatus[] | undefined {
    const hasAcValue = networkAssetBlockStatus?.includes(BlockStatus.BLOCKED)
    if (acEnabled) {
        return networkAssetBlockStatus
    }
    if (!networkAssetBlockStatus) {
        return [BlockStatus.TRUSTED, BlockStatus.UNDECIDED]
    }
    if (hasAcValue) {
        return networkAssetBlockStatus.filter((value) => value !== BlockStatus.BLOCKED)
    }
    return networkAssetBlockStatus
}

export function getMacAddressDisplayValue(macAddress: string | null | undefined): string {
    return !macAddress || macAddress.length == 0 ? 'N/A' : macAddress
}

export function getFirstDetectedDisplayValue(firstDetected: string | null | undefined): string {
    return !firstDetected ? 'Unknown' : moment(firstDetected).fromNow()
}
export function getHostnameDisplayValue(hostname: string | null | undefined): string {
    return !hostname || hostname.length == 0 ? 'Unknown' : hostname
}
export function getDisplayNameOfAsset(nodeData: NodeData | undefined): string {
    if (nodeData) {
        return (
            // TODO: Fix this the next time the file is edited.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            nodeData.alias ??
            nodeData.hostName ??
            nodeData.ipAddress ??
            nodeData.macAddress ??
            'Unknown'
        )
    }
    return 'Unknown'
}
